import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { AuthStore } from '@wheere-front/shared-auth';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { ConsoleCaptureService } from './console-capture.service';

/**
 * Base URL the bug-report POST goes to — same target as {@code USERMANAGER_BASE_PATH}
 * historically, but declared locally so libs/shared-ui (type:ui) doesn't depend on
 * domain-users (type:feature). Each app provides this in {@code app.config.ts}.
 *
 * <p>The factory defaults to {@code null} (instead of throwing on injection) so the
 * service can be constructed even when no provider is wired. The button consumer is
 * expected to inject this token directly and hide itself when the value is null —
 * see {@link BugReportButtonComponent}. {@code BugReportService.submit} also guards
 * against null and surfaces a clear runtime error if invoked anyway.
 */
export const BUG_REPORT_BASE_PATH = new InjectionToken<string | null>('BUG_REPORT_BASE_PATH', {
  factory: () => null,
  providedIn: 'root',
});

export type BugReportType = 'BUG' | 'IDEA';
export type BugSeverity = 'LOW' | 'MEDIUM' | 'HIGH';

/**
 * Tag identifying which app a bug report comes from (e.g. {@code 'wheere-integrator'},
 * {@code 'wheere-customer'}). Stamped on the payload as {@code appVersion} so the receiving
 * usermanager + Notion + Slack can route / filter per source. Each app provides its own value
 * in {@code app.config.ts}; defaults to {@code 'wheere-app'} if the consumer forgets.
 */
export const BUG_REPORT_APP_NAME = new InjectionToken<string>('BUG_REPORT_APP_NAME', {
  factory: () => 'wheere-app',
  providedIn: 'root',
});

/** What the user fills in the dialog. */
export interface BugReportInput {
  type: BugReportType;
  severity: BugSeverity;
  title: string;
  description: string;
  /** PNG data URL captured via html2canvas, or null if the user opted out / capture failed. */
  screenshot: string | null;
  /** Whether to attach the captured console logs. */
  includeLogs: boolean;
}

/** Full payload sent to the backend — user input + auto-captured context. */
interface BugReportPayload extends Omit<BugReportInput, 'screenshot' | 'includeLogs'> {
  route: string;
  consoleLogs: string | null;
  appVersion: string;
  userAgent: string;
  viewport: string;
  locale: string;
  reporterEmail: string | null;
  reporterId: string | null;
  organizationId: string | null;
  roles: string[];
  /** Raw base64 PNG (no `data:` prefix) so the backend can decode it directly. */
  screenshotBase64: string | null;
}

/**
 * Collects a bug / idea report from the UI and ships it to usermanager
 * ({@code POST /bug-reports}), which fans it out to Notion (a row) + Slack (a message).
 *
 * <p>Context (route, reporter, org, app version, viewport, user-agent) is captured
 * automatically so the reporter only types a title + description. The screenshot is
 * produced client-side with html2canvas — lazy-imported so the ~50 kB lib isn't in the
 * initial bundle.
 *
 * <p>Originally lived in {@code apps/wheere-integrator/src/app/data/bug-report.service.ts};
 * promoted here so {@code wheere-customer} can mount the same button.
 */
@Injectable({ providedIn: 'root' })
export class BugReportService {
  #http = inject(HttpClient);
  #base = inject(BUG_REPORT_BASE_PATH);
  #appName = inject(BUG_REPORT_APP_NAME);
  #auth = inject(AuthStore);
  #router = inject(Router);
  #transloco = inject(TranslocoService);
  #console = inject(ConsoleCaptureService);

  /** Capture the current page as a PNG data URL. Best-effort: returns null on any failure
   * (html2canvas chokes on some cross-origin images / unsupported CSS). Downscaled to keep
   * the payload small. */
  async captureScreenshot(): Promise<string | null> {
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        scale: Math.min(window.devicePixelRatio || 1, 1) * 0.6,
        logging: false,
        useCORS: true,
        backgroundColor: getComputedStyle(document.body).backgroundColor || '#ffffff',
      });
      return canvas.toDataURL('image/png');
    } catch {
      return null;
    }
  }

  async submit(input: BugReportInput): Promise<void> {
    if (!this.#base) {
      // The consuming app didn't provide BUG_REPORT_BASE_PATH. Caller — typically the
      // button component — is supposed to hide itself in this case, so reaching here
      // means a misconfiguration. Throwing surfaces it in the dialog's error state.
      throw new Error('BUG_REPORT_BASE_PATH not provided — bug reports cannot be submitted.');
    }
    const payload: BugReportPayload = {
      type: input.type,
      severity: input.severity,
      title: input.title.trim(),
      description: input.description.trim(),
      route: typeof window !== 'undefined' ? window.location.href : this.#router.url,
      appVersion: this.#appName,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      viewport: typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : '',
      locale: this.#transloco.getActiveLang(),
      reporterEmail: this.#auth.email() ?? null,
      reporterId: this.#auth.idUser() ?? null,
      organizationId: this.#auth.tenantOrganizationId() || null,
      roles: this.#auth.roles() ?? [],
      // Strip any `data:image/<type>;base64,` prefix — the backend wants raw base64.
      screenshotBase64: input.screenshot ? input.screenshot.replace(/^data:image\/\w+;base64,/, '') : null,
      consoleLogs: input.includeLogs ? this.#console.snapshot() : null,
    };
    await firstValueFrom(this.#http.post<void>(`${this.#base}/bug-reports`, payload));
  }
}
